import {Palette} from 'hubbl-shared';
import moment from 'moment';
import {memo, useMemo, useCallback} from 'react';
import styled from 'styled-components';
import {EventDTO} from '../../models/event/EventDTO';
import {EventCard} from '../social-activity-cards';
import {LoadPreviousEventsButton} from './components/load-previous-events-button/LoadPreviousEventsButton';
import {Timestamp} from 'firebase/firestore';

type TimelineDate = {
  day: string;
  month: string;
  year: number;
  isToday: boolean;
  isPast: boolean;
};

type GroupedEvents = Record<
  string,
  {
    date: TimelineDate;
    events: EventDTO[];
  }
>;

interface TimelineProps {
  events: EventDTO[];
  onEventClick: (eventId: string) => void;
  showLoadPreviousButton?: boolean;
  onLoadPrevious?: () => Promise<void>;
  hasMorePast?: boolean;
}

export const Timeline = memo(
  ({
    events,
    onEventClick,
    showLoadPreviousButton = false,
    onLoadPrevious,
    hasMorePast = false,
  }: TimelineProps) => {
    const formatTimelineDate = useCallback((date: Timestamp) => {
      const eventDate = date.toDate();
      return {
        day: moment(eventDate).format('DD'),
        month: moment(eventDate).format('MMM'),
        year: eventDate.getFullYear(),
        isToday: moment(eventDate).isSame(moment(), 'day'),
        isPast: moment(eventDate).isBefore(moment(), 'day'),
      };
    }, []);

    const handleEventClick = useCallback(
      (eventId: string) => {
        onEventClick(eventId);
      },
      [onEventClick],
    );

    const memoizedEvents = useMemo(() => {
      // Group events by date
      const groupedEvents = events.reduce((groups, event) => {
        const timelineDate = formatTimelineDate(event.startDateTime);
        const dateKey = `${timelineDate.year}-${timelineDate.month}-${timelineDate.day}`;

        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: timelineDate,
            events: [],
          };
        }

        groups[dateKey].events.push(event);
        return groups;
      }, {} as GroupedEvents);

      // Convert grouped events to timeline items
      return Object.values(groupedEvents)
        .map((group, groupIndex) => {
          return group.events.map((event, eventIndex) => {
            const isFirstEventOfDay = eventIndex === 0;
            const isPast = group.date.isPast;

            return (
              <TimelineItem key={event.id}>
                {isFirstEventOfDay && (
                  <TimelineDate>
                    <DateCircle
                      isToday={group.date.isToday}
                      isPast={group.date.isPast}>
                      <DateDay>{group.date.day}</DateDay>
                      <DateMonth>{group.date.month}</DateMonth>
                    </DateCircle>
                  </TimelineDate>
                )}

                {!isFirstEventOfDay && <TimelineDateSpacer />}

                <EventCard
                  event={event}
                  isPast={isPast}
                  onClick={() => handleEventClick(event.id)}
                />
              </TimelineItem>
            );
          });
        })
        .flat();
    }, [events, formatTimelineDate, handleEventClick]);

    if (events.length === 0) {
      return null;
    }

    return (
      <div style={{width: '90%'}}>
        <TimelineContainer>
          {/* Single timeline line behind all circles */}
          <TimelineLine />

          {memoizedEvents}

          {/* Load Previous Events button at the bottom */}
          {showLoadPreviousButton && hasMorePast && onLoadPrevious && (
            <LoadPreviousEventsButton onLoadPrevious={onLoadPrevious} />
          )}
        </TimelineContainer>
      </div>
    );
  },
);

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  padding-bottom: 2rem;
  overflow: visible;
  width: 100%;
  min-width: 0;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    gap: 1rem;
    padding-bottom: 1rem;
  }
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  position: relative;
  width: 100%;
  min-width: 0;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    gap: 1rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TimelineDate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  position: relative;
  justify-content: center;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    min-width: 60px;
    align-self: flex-start;
  }
`;

const TimelineDateSpacer = styled.div`
  min-width: 80px;
  position: relative;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    min-width: 60px;
  }
`;

const DateCircle = styled.div<{isToday: boolean; isPast: boolean}>`
  width: 60px;
  height: 60px;
  border-radius: 24px;
  background: ${props => {
    if (props.isToday) return Palette.primaryDustySky;
    if (props.isPast) return Palette.almostBlack250;
    return Palette.white;
  }};
  color: ${Palette.almostBlack};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    border-radius: 20px;
  }
`;

const DateDay = styled.div`
  font-size: 1.25rem;
  line-height: 1;
  margin-bottom: -2px;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const DateMonth = styled.div`
  font-size: 0.75rem;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const TimelineLine = styled.div`
  position: absolute;
  top: 30px;
  left: 40px;
  width: 3px;
  height: calc(100% - 90px);
  background: ${Palette.almostBlackTransparent10};
  z-index: 0;
  border-radius: 2px;
  pointer-events: none;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    top: 25px;
    left: 30px;
    height: calc(100% - 70px);
  }
`;
